import React from "react";

import {
  COLOR_ABSENT,
  COLOR_PRESENT,
  COLOR_TARDY,
} from "redux/constants";

const fmtSeconds = (seconds, negate = false) => {
  const total = Math.round(Math.abs(seconds))
  const m = Math.floor(total / 60)
  const s = total % 60
  const sign = negate ? '-' : ''
  return `${sign}${m}:${s.toString().padStart(2, '0')}`
}

const TickMark = ({ x, label, tooltip, barBottom, level = 0, renderPass }) => {
  const lineLen = 8 + level * 20
  if (renderPass === 'line') {
    return <line x1={x} y1={barBottom} x2={x} y2={barBottom + lineLen} stroke="#888" strokeWidth="1.5"><title>{tooltip || label}</title></line>
  }
  return <text x={x} y={barBottom + lineLen + 14} textAnchor="middle" fontSize="14" fill="#494949"><title>{tooltip || label}</title>{label}</text>
}

const assignLevels = (ticks, threshold = 52) => {
  const sorted = [...ticks].sort((a, b) => a.x - b.x)
  const levels = new Array(sorted.length).fill(0)
  for (let i = 1; i < sorted.length; i++) {
    const usedLevels = new Set()
    for (let j = 0; j < i; j++) {
      if (sorted[i].x - sorted[j].x < threshold) usedLevels.add(levels[j])
    }
    let level = 0
    while (usedLevels.has(level)) level++
    levels[i] = level
  }
  return sorted.map((t, i) => ({ ...t, level: levels[i] }))
}

const FIXED_CONTENT_WIDTH = 600      // fixed pixel width of the bar content area (never changes)
const PERIOD_LENGTH_SECONDS = 3600  // 60-minute display window
const H_MARGIN = 30                 // horizontal padding on each side
const BAR_TOP = 18
const INNER_PADDING = 4

class BellRuleSVGHorizontal extends React.Component {
  render() {
    const {
      bellPeriodPreStartPresentSeconds,
      bellPeriodPostStartTardySeconds,
      bellPeriodPostStartAbsentSeconds,
      positiveAttendanceIsEnabled = false,
      style = {},
      subPeriodHeight = 30,
      subPeriodSpacing = 5,
    } = this.props

    const isPreStartActive = bellPeriodPreStartPresentSeconds !== -1
    const isTardyActive = bellPeriodPostStartTardySeconds !== -1
    const isAbsentActive = bellPeriodPostStartAbsentSeconds !== -1

    const showEarlyPresent = positiveAttendanceIsEnabled && isPreStartActive && bellPeriodPreStartPresentSeconds > 0
    const showOnTimePresent = positiveAttendanceIsEnabled

    // Time range in seconds relative to bell (t=0).
    // tMin is negative when early present extends before the bell.
    // tMax expands beyond the default 60-minute window when tardy/absent thresholds exceed it.
    const tMin = showEarlyPresent ? -bellPeriodPreStartPresentSeconds : 0
    const tMax = Math.max(
      PERIOD_LENGTH_SECONDS,
      isTardyActive  ? bellPeriodPostStartTardySeconds + 300 : 0,
      isAbsentActive ? bellPeriodPostStartAbsentSeconds + 300 : 0,
    )

    const PIXELS_PER_SECOND = FIXED_CONTENT_WIDTH / (tMax - tMin)

    const timeToX = (t) => H_MARGIN + Math.round((t - tMin) * PIXELS_PER_SECOND)

    const xBell      = timeToX(0)
    const xPeriodEnd = timeToX(tMax)
    const xPreStart  = showEarlyPresent ? timeToX(-bellPeriodPreStartPresentSeconds) : xBell
    const xTardy     = isTardyActive    ? timeToX(bellPeriodPostStartTardySeconds)   : xPeriodEnd
    const xAbsent    = isAbsentActive   ? timeToX(bellPeriodPostStartAbsentSeconds)  : xPeriodEnd

    const containerX     = xBell
    const containerWidth = xPeriodEnd - xBell

    const barBottom     = BAR_TOP + subPeriodHeight
    const innerBarTop   = BAR_TOP + INNER_PADDING
    const innerBarHeight = subPeriodHeight - INNER_PADDING * 2

    const earlyPresentMinWidth = 2
    const earlyPresentRawWidth = showEarlyPresent ? Math.max(0, containerX - xPreStart - subPeriodSpacing) : 0
    const earlyPresentWidth    = showEarlyPresent ? Math.max(earlyPresentMinWidth, earlyPresentRawWidth) : 0

    const earlyPresentX = showEarlyPresent && earlyPresentRawWidth < earlyPresentMinWidth
      ? containerX - subPeriodSpacing - earlyPresentMinWidth
      : xPreStart

    const onTimePresentEndX  = isTardyActive ? xTardy : (isAbsentActive ? xAbsent : xPeriodEnd)
    const onTimePresentWidth = Math.max(0, onTimePresentEndX - containerX)
    const tardyWidth         = isTardyActive  ? Math.max(0, (isAbsentActive ? xAbsent : xPeriodEnd) - xTardy) : 0
    const absentWidth        = isAbsentActive ? Math.max(0, xPeriodEnd - xAbsent) : 0

    const innerClipId    = `inner-bar-clip-${Math.random().toString(36).slice(2)}`
    const innerClipX     = containerX + INNER_PADDING
    const innerClipWidth = containerWidth - INNER_PADDING * 2

    const xBellTick = xBell + 4
    const rawTicks = [
      { x: xBellTick, label: '0:00', tooltip: 'Bell time' },
      ...(showEarlyPresent
        ? [{ x: xPreStart, label: fmtSeconds(bellPeriodPreStartPresentSeconds, true), tooltip: `Early present starts at ${fmtSeconds(bellPeriodPreStartPresentSeconds, true)}` }]
        : []),
      ...(isTardyActive ? [{
        x: Math.max(xTardy, xBellTick), label: fmtSeconds(bellPeriodPostStartTardySeconds),
        tooltip: xTardy <= xBellTick ? `Bell time. Tardy starts at ${fmtSeconds(bellPeriodPostStartTardySeconds)}` : `Tardy starts at ${fmtSeconds(bellPeriodPostStartTardySeconds)}`
      }] : []),
      ...(isAbsentActive ? [{
        x: Math.max(xAbsent, xBellTick), label: fmtSeconds(bellPeriodPostStartAbsentSeconds),
        tooltip: xAbsent <= xBellTick ? `Bell time. Absent starts at ${fmtSeconds(bellPeriodPostStartAbsentSeconds)}` : `Absent starts at ${fmtSeconds(bellPeriodPostStartAbsentSeconds)}`
      }] : []),
    ]

    // When two marks land on the same x, keep only the last (absent > tardy > present)
    const uniqueRawTicks = rawTicks.filter((tick, i) =>
      !rawTicks.slice(i + 1).some(t => t.x === tick.x)
    )
    const ticks = assignLevels(uniqueRawTicks, 40)
    const maxLevel = ticks.reduce((max, t) => Math.max(max, t.level), 0)

    const viewBoxWidth  = xPeriodEnd + H_MARGIN
    const viewBoxHeight = barBottom + 8 + maxLevel * 20 + 24

    return <svg style={style} xmlns="http://www.w3.org/2000/svg" viewBox={`0 0 ${viewBoxWidth} ${viewBoxHeight}`}>
      <defs>
        <clipPath id={innerClipId}>
          <rect rx="2" x={innerClipX} y={innerBarTop} width={innerClipWidth} height={innerBarHeight}/>
        </clipPath>
      </defs>

      {/* Shared outer border for in-class bars */}
      <rect rx="4" x={containerX} y={BAR_TOP} width={containerWidth} height={subPeriodHeight} fill="none" stroke="#bbb" strokeWidth="3"/>

      {/* Early present bar */}
      {showEarlyPresent &&
        <rect rx="2" x={earlyPresentX} y={innerBarTop} width={earlyPresentWidth} height={innerBarHeight} fill={COLOR_PRESENT} stroke="none">
          <title>Early / On-Time Present ({fmtSeconds(bellPeriodPreStartPresentSeconds, true)} before bell)</title>
        </rect>
      }

      <g clipPath={`url(#${innerClipId})`}>
        {showOnTimePresent &&
          <rect x={containerX} y={innerBarTop} width={onTimePresentWidth} height={innerBarHeight} fill={COLOR_PRESENT}>
            <title>Present (on time)</title>
          </rect>
        }
        {isTardyActive &&
          <rect x={xTardy} y={innerBarTop} width={tardyWidth} height={innerBarHeight} fill={COLOR_TARDY}>
            <title>Tardy (starts at {fmtSeconds(bellPeriodPostStartTardySeconds)})</title>
          </rect>
        }
        {isAbsentActive &&
          <rect x={xAbsent} y={innerBarTop} width={absentWidth} height={innerBarHeight} fill={COLOR_ABSENT}>
            <title>Absent (starts at {fmtSeconds(bellPeriodPostStartAbsentSeconds)})</title>
          </rect>
        }
      </g>
      
      {/* Separator lines between inner bars */}
      {isTardyActive && showOnTimePresent && onTimePresentWidth > 0 && tardyWidth > 0 &&
        <line x1={xTardy} y1={innerBarTop} x2={xTardy} y2={innerBarTop + innerBarHeight} stroke="white" strokeWidth="2" />
      }
      {isAbsentActive && (isTardyActive || showOnTimePresent) && (tardyWidth > 0 || onTimePresentWidth > 0) &&
        <line x1={xAbsent} y1={innerBarTop} x2={xAbsent} y2={innerBarTop + innerBarHeight} stroke="white" strokeWidth="2"/>
      }

      {ticks.map((tick, i) => (
        <TickMark key={i} x={tick.x} label={tick.label} tooltip={tick.tooltip} barBottom={barBottom} level={tick.level} renderPass="line" />
      ))}
      {ticks.map((tick, i) => (
        <TickMark key={i} x={tick.x} label={tick.label} tooltip={tick.tooltip} barBottom={barBottom} level={tick.level} renderPass="text" />
      ))}
    </svg>
  }
}

export default BellRuleSVGHorizontal
